import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { differenceInHours, isValid as isDateValid } from 'date-fns';

import { EventView } from '@ndvr/customer-api';

import { CustomerApi } from '../../API';
import { convertBackendTimeToBrowser, formatDate, isFullDayEvent } from '../../utils';
import { useAuthContext } from '../auth/Auth';

export const useConnectCalendar = () => {
    const { user } = useAuthContext();
    return useMutation({
        mutationFn: async () => {
            const [payload] = await CustomerApi().connectCalendar(user!.personOrgGUID);
            if (!payload?.redirectUrl) {
                throw new Error('Redirect URL missing in response');
            }
            window.location.href = payload.redirectUrl;
        },
    });
};

export const useHasConnectedCalendar = () => {
    const { user } = useAuthContext();
    return useQuery({
        queryKey: ['personOrg', user?.personOrgGUID, 'calendar', 'hasConnectedCalendar'],
        queryFn: async () => {
            const [hasConnectedCalendar] = await CustomerApi().hasConnectedCalendar(user!.personOrgGUID);
            return hasConnectedCalendar;
        },
    });
};

export const useIsZoomConnected = () => {
    const { user } = useAuthContext();
    return useQuery({
        queryKey: ['personOrg', user?.personOrgGUID, 'calendar', 'isZoomConnected'],
        queryFn: async () => {
            const [isZoomConnected] = await CustomerApi().isZoomConnected(user!.personOrgGUID);
            return isZoomConnected;
        },
    });
};

export const useConnectZoom = () => {
    const { user } = useAuthContext();
    return useMutation({
        mutationFn: async () => {
            const [payload] = await CustomerApi().connectZoom(user!.personOrgGUID);
            if (!payload?.redirectUrl) {
                throw new Error('Redirect URL missing in response');
            }
            window.location.href = payload.redirectUrl;
        },
    });
};

export const useCalendarViews = ({ fromDate, toDate }: { fromDate?: Date; toDate?: Date }) => {
    const { user } = useAuthContext();
    return useQuery({
        queryKey: [
            'personOrg',
            user?.personOrgGUID,
            'calendar',
            'views',
            `${fromDate?.toISOString()}-${toDate?.toISOString()}`,
        ],
        enabled: !!user && isDateValid(fromDate) && isDateValid(toDate),
        // Keep showing the previous range's events while the next range loads, so stepping
        // never blanks the calendar or the notes list mid-fetch.
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const [calendarViews] = await CustomerApi().getCalendarViews(user!.personOrgGUID, {
                fromDate: formatDate(fromDate, 'SERVER'),
                toDate: formatDate(toDate, 'SERVER'),
            });
            const calendarEvents = calendarViews?.flatMap((view) => view.events) ?? [];
            const eventViewMap = new Map<string, EventView>();
            calendarEvents.forEach((event) => eventViewMap.set(event.eventGUID, event));
            return {
                calendarViews,
                calendarEvents,
                fullCalendarEvents: calendarEvents
                    .map(mapToFullCalendarEvent)
                    .sort((a, b) => a.start.getTime() - b.start.getTime()),
                eventViewMap,
            };
        },
    });
};

export type FullCalendarEvent = {
    id: string;
    start: Date;
    end: Date;
    title: string | undefined;
    allDay: boolean;
};

const SYNCING_THRESHOLD_HOURS = 1;

export const useIsSyncingCalendar = () => {
    const { user } = useAuthContext();
    return useQuery({
        queryKey: ['personOrg', user?.personOrgGUID, 'calendar', 'isSyncing'],
        queryFn: async () => {
            const [calendarViews] = await CustomerApi().getCalendarViews(user!.personOrgGUID, {
                fromDate: formatDate(new Date(), 'SERVER'),
                toDate: formatDate(new Date(), 'SERVER'),
            });
            return (calendarViews ?? []).some((view) => {
                if (!view.createdAt) return false;
                return differenceInHours(new Date(), new Date(view.createdAt)) < SYNCING_THRESHOLD_HOURS;
            });
        },
        enabled: !!user,
    });
};

function mapToFullCalendarEvent(eventView: EventView): FullCalendarEvent {
    const startLocal = convertBackendTimeToBrowser(eventView.startTime, eventView.startTimeZone);
    const endLocal = convertBackendTimeToBrowser(eventView.endTime, eventView.endTimeZone);

    return {
        id: eventView.eventGUID,
        title: eventView?.title ?? '(No Title)',
        start: startLocal,
        end: endLocal,
        allDay: isFullDayEvent(startLocal, endLocal),
    };
}
